<template>
  <!-- Sprint 6 follow-up — Contacts subtab extracted from NcrmMailjetDataPanel
       so the parent stays under the size policy. Self-contained: own state,
       own fetcher, own dialog. Only `accountId` flows in; events go out via
       `notify`. -->
  <div>
    <!-- Sprint 6 follow-up — search + stats window + server-side filters
         (list / status / subscription / activity) on a single wrapping row.
         All filters collapse to "no filter" when left at their default. -->
    <div class="d-flex align-center ga-3 mb-3 flex-wrap">
      <v-text-field
        v-model="contactsSearch"
        :label="$t('ncrm.integrations.mailjet.panel.search')"
        prepend-inner-icon="mdi-magnify"
        hide-details
        clearable
        style="min-width: 200px; max-width: 240px;"
        @update:model-value="onContactsSearchInput"
      />
      <v-select
        v-model="contactsWindow"
        :items="contactsWindowOptions"
        :label="$t('ncrm.integrations.mailjet.panel.window.label')"
        hide-details
        style="min-width: 150px; max-width: 180px;"
      />
      <v-select
        v-model="filterListIds"
        :items="listOptions"
        :label="$t('ncrm.integrations.mailjet.panel.filters.lists')"
        item-title="name"
        item-value="id"
        hide-details
        multiple
        chips
        closable-chips
        clearable
        :loading="listsLoading"
        style="min-width: 200px; max-width: 260px;"
      />
      <v-select
        v-model="filterExcluded"
        :items="excludedOptions"
        :label="$t('ncrm.integrations.mailjet.panel.filters.status')"
        hide-details
        style="min-width: 150px; max-width: 190px;"
      />
      <v-select
        v-model="filterSubscription"
        :items="subscriptionOptions"
        :label="$t('ncrm.integrations.mailjet.panel.filters.subscription')"
        hide-details
        style="min-width: 150px; max-width: 190px;"
      />
      <v-select
        v-model="filterActivity"
        :items="activityOptions"
        :label="$t('ncrm.integrations.mailjet.panel.filters.activity')"
        hide-details
        style="min-width: 150px; max-width: 190px;"
      />
      <v-btn
        v-if="hasActiveFilters"
        variant="text"
        size="small"
        prepend-icon="mdi-filter-off-outline"
        @click="clearFilters"
      >
        {{ $t("ncrm.integrations.mailjet.panel.filters.clear") }}
      </v-btn>
      <v-spacer />
      <span class="text-caption text-medium-emphasis">
        {{ $t("ncrm.integrations.mailjet.panel.totalLabel", { total: contactsTotal }) }}
      </span>
    </div>
    <v-data-table-server
      :headers="contactsHeaders"
      :items="contacts"
      :items-length="contactsTotal"
      :loading="contactsLoading"
      v-model:items-per-page="contactsPerPage"
      v-model:page="contactsPage"
      :items-per-page-options="[25, 50, 100, 200]"
      density="comfortable"
      class="elevation-0"
      item-key="id"
    >
      <template #item.stats="{ item }">
        <div class="ncrm-mj-contacts__stats">
          <v-tooltip
            v-for="key in STAT_KEYS"
            :key="key"
            location="top"
          >
            <template #activator="{ props: tipProps }">
              <v-chip
                v-bind="tipProps"
                size="x-small"
                :color="statColor(key)"
                variant="tonal"
                :class="{ 'ncrm-mj-contacts__stat--zero': !item.stats?.[key] }"
              >
                <v-icon start size="11">{{ STAT_ICONS[key] }}</v-icon>
                {{ item.stats?.[key] || 0 }}
              </v-chip>
            </template>
            {{ $t(`ncrm.integrations.mailjet.activity.stats30Days.${key}`) }}: {{ item.stats?.[key] || 0 }}
          </v-tooltip>
        </div>
      </template>
      <template #item.lists="{ item }">
        <div v-if="item.memberships?.length" class="d-flex ga-1 flex-wrap">
          <v-chip
            v-for="m in item.memberships.slice(0, 2)"
            :key="m.list_id"
            size="x-small"
            :color="m.is_unsubscribed ? 'error' : 'success'"
          >
            {{ m.list_name }}
          </v-chip>
          <v-tooltip v-if="item.memberships.length > 2" location="top">
            <template #activator="{ props: tipProps }">
              <v-chip
                v-bind="tipProps"
                size="x-small"
                variant="outlined"
              >
                +{{ item.memberships.length - 2 }}
              </v-chip>
            </template>
            <div
              v-for="m in item.memberships.slice(2)"
              :key="m.list_id"
            >
              {{ m.list_name }}<span v-if="m.is_unsubscribed"> ({{ $t("ncrm.integrations.mailjet.panel.unsubscribed") }})</span>
            </div>
          </v-tooltip>
        </div>
        <span v-else class="text-medium-emphasis">—</span>
      </template>
      <template #item.is_excluded_from_campaigns="{ item }">
        <v-chip v-if="item.is_excluded_from_campaigns" size="x-small" color="warning">
          {{ $t("ncrm.integrations.mailjet.panel.excluded") }}
        </v-chip>
        <span v-else class="text-medium-emphasis">—</span>
      </template>
      <template #item.last_synced_at="{ item }">{{ formatDate(item.last_synced_at) }}</template>
      <template #item.actions="{ item }">
        <v-tooltip location="top">
          <template #activator="{ props: tipProps }">
            <v-btn
              v-bind="tipProps"
              icon
              size="small"
              variant="text"
              color="error"
              @click="openUnsubDialog(item)"
            >
              <v-icon size="18">mdi-email-off-outline</v-icon>
            </v-btn>
          </template>
          {{ $t("ncrm.integrations.mailjet.panel.unsubAll.tooltip") }}
        </v-tooltip>
      </template>
    </v-data-table-server>

    <!-- Sprint 6 follow-up — bulk unsubscribe confirmation. -->
    <v-dialog v-model="unsubDialog.show" max-width="480">
      <v-card>
        <v-card-title>
          {{ $t("ncrm.integrations.mailjet.panel.unsubAll.title") }}
        </v-card-title>
        <v-card-text>
          <p class="mb-2">
            {{
              $t("ncrm.integrations.mailjet.panel.unsubAll.message", {
                email: unsubDialog.email,
                n: unsubDialog.listsCount,
              })
            }}
          </p>
          <v-alert
            v-if="unsubDialog.listsCount === 0"
            type="info"
            density="compact"
          >
            {{ $t("ncrm.integrations.mailjet.panel.unsubAll.noActive") }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="unsubDialog.loading" @click="unsubDialog.show = false">
            {{ $t("ncrm.integrations.mailjet.panel.unsubAll.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="unsubDialog.loading"
            :disabled="unsubDialog.listsCount === 0"
            @click="confirmUnsubAll"
          >
            {{ $t("ncrm.integrations.mailjet.panel.unsubAll.confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";

// Display order + iconography for the 5 per-contact stats mini-chips.
// Mirrors STAT_KEYS in NcrmMailjetActivityPanel.vue so the two views feel
// consistent.
const STAT_KEYS = ["sent", "open", "click", "bounce", "unsub"];
const STAT_ICONS = {
  sent: "mdi-send",
  open: "mdi-email-open-outline",
  click: "mdi-cursor-default-click-outline",
  bounce: "mdi-alert-circle-outline",
  unsub: "mdi-cancel",
};

export default {
  name: "NcrmMailjetContactsTab",
  props: {
    accountId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ["notify"],
  data() {
    return {
      STAT_KEYS,
      STAT_ICONS,
      contacts: [],
      contactsTotal: 0,
      contactsLoading: false,
      contactsPage: 1,
      contactsPerPage: 50,
      contactsSearch: "",
      contactsSearchTimer: null,
      // "all" maps to a null windowDays on the backend (full history;
      // mailjet_events isn't pruned). Other values are integer days.
      contactsWindow: "all",
      // Server-side filters. Defaults below mean "no filter".
      filterListIds: [],
      filterExcluded: "all",
      filterSubscription: "all",
      filterActivity: "all",
      // Lists used to populate the membership filter dropdown.
      lists: [],
      listsLoading: false,
      unsubDialog: {
        show: false,
        email: "",
        listsCount: 0,
        contactId: null,
        loading: false,
      },
    };
  },
  computed: {
    contactsHeaders() {
      return [
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.email"), key: "email" },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.fullName"), key: "name" },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.stats"), key: "stats", sortable: false, width: 230 },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.lists"), key: "lists", sortable: false, width: 280 },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.excludedFromCampaigns"), key: "is_excluded_from_campaigns", sortable: false },
        { title: this.$t("ncrm.integrations.mailjet.panel.headers.lastSynced"), key: "last_synced_at" },
        { title: "", key: "actions", sortable: false, width: 56, align: "end" },
      ];
    },
    contactsWindowOptions() {
      return [
        { value: "all", title: this.$t("ncrm.integrations.mailjet.panel.window.all") },
        { value: "30", title: this.$t("ncrm.integrations.mailjet.panel.window.d30") },
        { value: "90", title: this.$t("ncrm.integrations.mailjet.panel.window.d90") },
        { value: "365", title: this.$t("ncrm.integrations.mailjet.panel.window.m12") },
      ];
    },
    // Only CRM-visible lists are valid filter targets (memberships hidden from
    // the UI are excluded from the contacts payload too).
    listOptions() {
      return (Array.isArray(this.lists) ? this.lists : []).filter((l) => l.show_in_crm_ui);
    },
    excludedOptions() {
      return [
        { value: "all", title: this.$t("ncrm.integrations.mailjet.panel.filters.statusAll") },
        { value: "excluded", title: this.$t("ncrm.integrations.mailjet.panel.filters.statusExcluded") },
        { value: "included", title: this.$t("ncrm.integrations.mailjet.panel.filters.statusIncluded") },
      ];
    },
    subscriptionOptions() {
      return [
        { value: "all", title: this.$t("ncrm.integrations.mailjet.panel.filters.subAll") },
        { value: "subscribed", title: this.$t("ncrm.integrations.mailjet.panel.filters.subSubscribed") },
        { value: "unsubscribed", title: this.$t("ncrm.integrations.mailjet.panel.filters.subUnsubscribed") },
      ];
    },
    activityOptions() {
      return [
        { value: "all", title: this.$t("ncrm.integrations.mailjet.panel.filters.activityAny") },
        ...STAT_KEYS.map((key) => ({
          value: key,
          title: this.$t(`ncrm.integrations.mailjet.activity.stats30Days.${key}`),
        })),
      ];
    },
    hasActiveFilters() {
      return (
        (this.filterListIds && this.filterListIds.length > 0) ||
        this.filterExcluded !== "all" ||
        this.filterSubscription !== "all" ||
        this.filterActivity !== "all"
      );
    },
  },
  watch: {
    accountId: {
      handler(id) {
        if (id) {
          this.contactsPage = 1;
          this.fetchLists();
          this.fetchContacts();
        }
      },
      immediate: true,
    },
    contactsPage() { this.fetchContacts(); },
    contactsPerPage() { this.contactsPage = 1; this.fetchContacts(); },
    contactsWindow() { this.contactsPage = 1; this.fetchContacts(); },
    filterListIds() { this.contactsPage = 1; this.fetchContacts(); },
    filterExcluded() { this.contactsPage = 1; this.fetchContacts(); },
    filterSubscription() { this.contactsPage = 1; this.fetchContacts(); },
    filterActivity() { this.contactsPage = 1; this.fetchContacts(); },
  },
  methods: {
    extractError(error, fallbackKey) {
      return error?.response?.data?.error || error?.message || this.$t(fallbackKey);
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return formatDisplayDateTime(value);
      } catch (_) {
        return String(value);
      }
    },
    // Color hint for the 5 mini stats chips. Mirrors the palette used by the
    // per-entity activity panel so the visual semantics stay consistent
    // between views.
    statColor(key) {
      if (key === "open" || key === "click") return "success";
      if (key === "bounce") return "warning";
      if (key === "unsub") return "error";
      return "info";
    },
    onContactsSearchInput() {
      if (this.contactsSearchTimer) clearTimeout(this.contactsSearchTimer);
      this.contactsSearchTimer = setTimeout(() => {
        this.contactsPage = 1;
        this.fetchContacts();
      }, 300);
    },
    // Load the account's CRM-visible lists for the membership filter dropdown.
    // Best-effort: a failure just leaves the list filter empty, other filters
    // still work.
    async fetchLists() {
      if (!this.accountId) return;
      this.listsLoading = true;
      try {
        const { data } = await axios.get(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/lists`
        );
        this.lists = Array.isArray(data?.lists) ? data.lists : [];
      } catch (_) {
        this.lists = [];
      } finally {
        this.listsLoading = false;
      }
    },
    clearFilters() {
      this.filterListIds = [];
      this.filterExcluded = "all";
      this.filterSubscription = "all";
      this.filterActivity = "all";
    },
    async fetchContacts() {
      if (!this.accountId) return;
      this.contactsLoading = true;
      try {
        const params = {
          limit: this.contactsPerPage,
          offset: (this.contactsPage - 1) * this.contactsPerPage,
          window: this.contactsWindow || "all",
        };
        if (this.contactsSearch?.trim()) params.search = this.contactsSearch.trim();
        if (this.filterListIds?.length) params.listIds = this.filterListIds.join(",");
        if (this.filterExcluded !== "all") params.excluded = this.filterExcluded;
        if (this.filterSubscription !== "all") params.subscription = this.filterSubscription;
        if (this.filterActivity !== "all") params.activity = this.filterActivity;
        const { data } = await axios.get(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/contacts`,
          { params }
        );
        this.contacts = Array.isArray(data?.rows) ? data.rows : [];
        this.contactsTotal = Number(data?.total || 0);
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message: this.extractError(error, "ncrm.integrations.mailjet.panel.errors.contacts"),
        });
      } finally {
        this.contactsLoading = false;
      }
    },
    openUnsubDialog(item) {
      if (!item?.id) return;
      // Count only the lists actually subscribed-active (memberships with
      // is_unsubscribed=true are excluded — the operator can't "unsub"
      // someone who's already unsubscribed from that list).
      const memberships = Array.isArray(item.memberships) ? item.memberships : [];
      const activeCount = memberships.filter((m) => !m.is_unsubscribed).length;
      this.unsubDialog = {
        show: true,
        email: item.email || "",
        listsCount: activeCount,
        contactId: item.id,
        loading: false,
      };
    },
    async confirmUnsubAll() {
      const { contactId, listsCount } = this.unsubDialog;
      if (!contactId || listsCount === 0) return;
      this.unsubDialog.loading = true;
      try {
        const { data } = await axios.post(
          `/ncrm/integrations/mailjet/accounts/${this.accountId}/contacts/${contactId}/unsubscribe-all`
        );
        const unsubbed = Array.isArray(data?.unsubscribed) ? data.unsubscribed.length : 0;
        const failed = Array.isArray(data?.failed) ? data.failed.length : 0;
        if (failed > 0) {
          this.$emit("notify", {
            color: "warning",
            message: this.$t("ncrm.integrations.mailjet.panel.unsubAll.alerts.partial", {
              ok: unsubbed,
              failed,
            }),
          });
        } else {
          this.$emit("notify", {
            color: "success",
            message: this.$t("ncrm.integrations.mailjet.panel.unsubAll.alerts.success", { n: unsubbed }),
          });
        }
        this.unsubDialog.show = false;
        await this.fetchContacts();
      } catch (error) {
        this.$emit("notify", {
          color: "error",
          message: this.extractError(error, "ncrm.integrations.mailjet.panel.unsubAll.errors.unsubAll"),
        });
      } finally {
        this.unsubDialog.loading = false;
      }
    },
    // Public API: lets the parent panel trigger a reload when it switches
    // tabs back into this view (preserves existing refreshActiveTab semantic).
    refresh() {
      this.contactsPage = 1;
      this.fetchContacts();
    },
  },
};
</script>

<style scoped>
.ncrm-mj-contacts__stats {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
}
.ncrm-mj-contacts__stat--zero {
  opacity: 0.4;
}
</style>
